import { memo } from 'react';

import styled from '@emotion/styled';
import { parseCheckedState, TableCell, TableHead, TableRow } from '@kc-console/common-ui';
import { t } from '@kc-console/core';

import DataTableCheckbox from './DataTableCheckbox';
import DataTableColumnVirtualizationSpacerCell from './DataTableColumnVirtualizationSpacerCell';
import DataTableTableHeadCell from './DataTableTableHeadCell';
import ExpandButton from './ExpandButton';
import { createBuiltinColumnVariableKeys } from './utils';

import type { ChangeEvent, JSX, MouseEvent, TouchEvent } from 'react';
import type { RowData } from '@tanstack/react-table';
import type { CheckedState } from '@kc-console/common-ui';
import type {
  DataTableColumnVirtualizationState,
  DataTableHeaderContent,
  DataTableHeaderGroupContent,
} from './types';

export interface DataTableTableHeadProps<TData extends RowData> {
  expandHeaderState: boolean;
  hasAutoPadColumn: boolean;
  hasExpandColumn: boolean;
  hasSelectionHeader: boolean;
  hasSelectionHeaderAsCheckbox: boolean;
  headerGroups: DataTableHeaderGroupContent<TData>[];
  onColumnResize: (
    event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>,
    columnId: string,
    cellElement: HTMLTableCellElement,
  ) => void;
  onColumnSizeReset: (columnId: string) => void;
  onExpandHeaderChange: (event: MouseEvent<HTMLButtonElement>, checked: boolean) => void;
  onSelectionHeaderChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onSortDirectionChange: (columnId: string, sortDirection: 'asc' | 'desc' | false) => void;
  selectionHeaderCheckedState: CheckedState;
  showExpandAllButton: boolean;
  columnVirtualization?: DataTableColumnVirtualizationState;
}

export default memo(function DataTableTableHead<TData extends RowData>(
  props: DataTableTableHeadProps<TData>,
) {
  const {
    columnVirtualization,
    expandHeaderState,
    hasAutoPadColumn,
    hasExpandColumn,
    hasSelectionHeader,
    hasSelectionHeaderAsCheckbox,
    headerGroups,
    onColumnResize,
    onColumnSizeReset,
    onExpandHeaderChange,
    onSelectionHeaderChange,
    onSortDirectionChange,
    selectionHeaderCheckedState,
    showExpandAllButton,
  } = props;

  const renderHeader = (header: DataTableHeaderContent<TData, unknown>) => (
    <DataTableTableHeadCell
      key={header.id}
      header={header}
      onColumnResize={onColumnResize}
      onColumnSizeReset={onColumnSizeReset}
      onSortDirectionChange={onSortDirectionChange}
    />
  );

  return (
    <TableHead>
      {headerGroups.map((headerGroup, headerGroupIndex, headerGroups) => {
        const { id: headerGroupId, headers } = headerGroup;

        const {
          left: leftHeaders,
          center: centerHeaders,
          right: rightHeaders,
        } = headers.reduce(
          (result, header) => {
            result[header.stickyPosition || 'center'].push(header);
            return result;
          },
          { left: [], center: [], right: [] } as Record<
            'left' | 'center' | 'right',
            DataTableHeaderContent<TData, unknown>[]
          >,
        );

        return (
          <TableRow key={headerGroupId}>
            {hasExpandColumn && (
              <TableCell
                padding="checkbox"
                size="small"
                sticky
                stickyPosition="left"
                style={{
                  width: `var(${createBuiltinColumnVariableKeys('expand').size})`,
                  left: `var(${createBuiltinColumnVariableKeys('expand').offset})`,
                }}
              >
                {showExpandAllButton && (
                  <ExpandButton
                    expanded={expandHeaderState}
                    onClick={event => onExpandHeaderChange(event, !expandHeaderState)}
                  />
                )}
              </TableCell>
            )}
            {hasSelectionHeader && (
              <TableCell
                padding="checkbox"
                sticky
                stickyPosition="left"
                style={{
                  width: `var(${createBuiltinColumnVariableKeys('selection').size})`,
                  left: `var(${createBuiltinColumnVariableKeys('selection').offset})`,
                }}
              >
                {headerGroups.length - 1 === headerGroupIndex &&
                  (hasSelectionHeaderAsCheckbox ? (
                    <DataTableCheckbox
                      {...parseCheckedState(selectionHeaderCheckedState)}
                      aria-label={t('ui:checkbox.select_all')}
                      onChange={onSelectionHeaderChange}
                    />
                  ) : null)}
              </TableCell>
            )}
            {leftHeaders.map(renderHeader)}
            {columnVirtualization?.leftSpacer && (
              <DataTableColumnVirtualizationSpacerCell spacer={columnVirtualization.leftSpacer} />
            )}
            {centerHeaders.map(renderHeader)}
            {columnVirtualization?.rightSpacer && (
              <DataTableColumnVirtualizationSpacerCell spacer={columnVirtualization.rightSpacer} />
            )}
            {hasAutoPadColumn && <AutoPadRightHeadCell />}
            {rightHeaders.map(renderHeader)}
          </TableRow>
        );
      })}
    </TableHead>
  );
}) as <TData extends RowData>(props: DataTableTableHeadProps<TData>) => JSX.Element;

const AutoPadRightHeadCell = styled(TableCell)`
  && {
    padding: 0;
    width: var(${createBuiltinColumnVariableKeys('autoPadRight').size});
  }
`;
